/**
 * What went wrong with the last app they tried.
 *
 * Verbi asks this twice during onboarding — "have you tried other language apps?" then
 * "why didn't it work for you?" with seven options — and only afterwards shows its
 * three differentiator cards (`docs/research/12-competitor-recordings-2026-10-19.md`
 * §2). It is the cheapest personalisation in their flow, and it arms the pitch with the
 * learner's own words instead of a guess.
 *
 * We can do something they cannot: the three complaints that dominate both competitors'
 * reviews are the three things this product was built against, so the answer selects a
 * promise we can actually keep rather than a slogan. A learner who says the last app
 * charged before they could try it hears the free-minutes commitment; one who says it
 * marked them wrong hears that we decline to score rather than guess.
 *
 * The options are the real review themes, in the learner's words: paywall and price
 * (19% of Pingo's complaints), accuracy (20% of Verbi's), and never actually speaking,
 * which is the category's founding complaint.
 */

/** Why the last app did not work, as the learner would say it. */
export const PRIOR_APP_FAILURES = [
  'wanted_money_first',
  'marked_me_wrong',
  'never_spoke',
  'lost_interest',
  'too_easy',
  'too_hard',
  'other',
] as const;

export type PriorAppFailure = (typeof PRIOR_APP_FAILURES)[number];

/** One promise we make, with the evidence that we keep it. */
export interface PromiseCard {
  /** Which product commitment this is, for the analytics and for the tests. */
  readonly id: 'free_minutes' | 'withheld_scores' | 'speaking_first' | 'dialect';
  readonly title: string;
  readonly body: string;
}

const CARDS: Readonly<Record<PromiseCard['id'], PromiseCard>> = {
  free_minutes: {
    id: 'free_minutes',
    title: 'You can speak before you pay',
    body: 'Ten minutes of real conversation a day, free, with no trial to cancel. We ask for money when you want more than that, not before you have had any.',
  },
  withheld_scores: {
    id: 'withheld_scores',
    title: "We say when we aren't sure",
    body: 'When the audio is too quiet or our engines disagree, you get told that — not a low score you have to argue with. And if we do mark you wrong, you can appeal it in one tap.',
  },
  speaking_first: {
    id: 'speaking_first',
    title: 'You speak in the first minute',
    body: 'Not a streak of tapping translations. Your first lesson is a conversation, scored on how you actually sounded.',
  },
  dialect: {
    id: 'dialect',
    title: 'The dialect you actually need',
    body: 'Mexican or Castilian, Brazilian or European — the words, the voice and the scoring all follow the one you picked, and the word screen shows you what the other regions say.',
  },
};

/**
 * The promise to lead with, given what went wrong last time.
 *
 * Exactly one card leads. Three promises shown at once is a brochure; one promise that
 * answers the sentence they just selected is an answer.
 */
export function promiseFor(failure: PriorAppFailure | null): PromiseCard {
  switch (failure) {
    case 'wanted_money_first':
      return CARDS.free_minutes;
    case 'marked_me_wrong':
      return CARDS.withheld_scores;
    case 'never_spoke':
    case 'too_easy':
      return CARDS.speaking_first;
    // A learner who found the last app too hard, lost interest, or will not say is not
    // telling us which promise matters, so they get the one the whole product is for.
    default:
      return CARDS.speaking_first;
  }
}

/**
 * Three cards, led by the one that answers them.
 *
 * The two that follow are chosen per lead rather than taken off a fixed list: a fixed
 * list left the dialect card unreachable for every answer, which is a promise written
 * and never shown. Three in total, because a fourth makes it a brochure.
 */
export function promisesFor(failure: PriorAppFailure | null): readonly PromiseCard[] {
  const lead = promiseFor(failure);
  const following: Readonly<Record<PromiseCard['id'], readonly PromiseCard['id'][]>> = {
    free_minutes: ['speaking_first', 'withheld_scores'],
    withheld_scores: ['speaking_first', 'free_minutes'],
    // The default lead, so this is the pair most learners see: what it costs, and the
    // one thing no competitor does at all.
    speaking_first: ['free_minutes', 'dialect'],
    dialect: ['speaking_first', 'free_minutes'],
  };
  return [lead, ...following[lead.id].map((id) => CARDS[id])];
}
